'use client';

import React from 'react';

/**
 * iOS UITableView grouped-inset style input container.
 * Adapts to light/dark backgrounds automatically.
 */
export function NativeGroupedInput({
  children,
  appBackgroundColor = '#FFFFFF',
}: {
  children: React.ReactNode;
  appBackgroundColor?: string;
}) {
  const items = React.Children.toArray(children).filter(Boolean);
  const isDark = isDarkBg(appBackgroundColor);

  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{
        backgroundColor: `var(--native-auth-line, ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'})`,
        borderRadius: 'var(--native-auth-radius, 12px)',
      }}
    >
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <div className="mx-4">
              <div
                style={{
                  borderTopWidth: 1,
                  borderTopStyle: 'solid',
                  borderTopColor: `var(--native-auth-line, ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'})`,
                }}
              />
            </div>
          )}
          <div className="px-4 py-3">{child}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * A single input row within the grouped container.
 */
export function NativeInputRow({
  label,
  children,
  appTextColor,
}: {
  label?: string;
  children: React.ReactNode;
  appTextColor?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <span
          className="text-xs font-medium"
          style={{ color: `var(--native-auth-muted, ${appTextColor ? `${appTextColor}80` : 'inherit'})`, opacity: appTextColor ? undefined : 0.5 }}
        >
          {label}
        </span>
      )}
      {children}
    </div>
  );
}

function isDarkBg(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}
